import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { StreamEvent } from "../../api/stream.type";
import {
  StreamCallbacks,
  useEventStreamLifecycle,
} from "./useEventStreamLifecycle";
import { useMarqueeMessages } from "./useMarqueeMessages";

interface EventStreamingContextType {
  events: StreamEvent[];
  streamedText: string;
  setStreamedText: (text: string) => void;
  streamingMessageId: string | null;
  isStreaming: boolean;
  eventSource: EventSource | null;
  marqueeMessages: string[];
}

interface EventStreamingControlsContextValue {
  startStreaming: (messageId: string) => void;
  stopStreaming: (options?: { preserveStreamText?: boolean }) => void;
  registerStreamCallbacks: (callbacks: StreamCallbacks | null) => void;
  setStreamedText: React.Dispatch<React.SetStateAction<string>>;
}

const EventStreamingStateContext =
  createContext<EventStreamingContextType | null>(null);
const EventStreamingControlsContext =
  createContext<EventStreamingControlsContextValue | null>(null);

export const useEventStreaming = () => {
  const context = useContext(EventStreamingStateContext);
  if (!context) {
    throw new Error(
      "useEventStreaming must be used within an EventStreamingWrapper"
    );
  }
  return context;
};

export const useEventStreamingControls = () => {
  const context = useContext(EventStreamingControlsContext);
  if (!context) {
    throw new Error(
      "useEventStreamingControls must be used within an EventStreamingWrapper"
    );
  }
  return context;
};

export const EventStreamingProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [events, setEvents] = useState<StreamEvent[]>([]);
  const [streamedText, setStreamedTextState] = useState("");

  const setStreamedText: React.Dispatch<React.SetStateAction<string>> =
    useCallback((value) => {
      setStreamedTextState((prev) => {
        const next = typeof value === "function" ? value(prev) : value;
        return next;
      });
    }, []);

  const {
    messages: marqueeMessages,
    pushMessage,
    clearMessages,
  } = useMarqueeMessages();

  const appendEvent = useCallback((event: StreamEvent) => {
    setEvents((prev) => [...prev, event]);
  }, []);

  const {
    streamingMessageId,
    isStreaming,
    eventSource,
    startStreaming,
    stopStreaming,
    registerStreamCallbacks,
  } = useEventStreamLifecycle({
    appendEvent,
    setStreamedText,
    pushMarqueeMessage: pushMessage,
    clearMarqueeMessages: clearMessages,
  });

  useEffect(() => {
    if (!isStreaming) {
      clearMessages();
    }
  }, [clearMessages, isStreaming]);

  const marqueeState = useMemo(() => [...marqueeMessages], [marqueeMessages]);

  const stateValue = useMemo(
    () => ({
      events,
      streamedText,
      setStreamedText,
      streamingMessageId,
      isStreaming,
      eventSource,
      marqueeMessages: marqueeState,
    }),
    [
      events,
      streamedText,
      streamingMessageId,
      isStreaming,
      eventSource,
      marqueeState,
    ]
  );

  const controlsValue = useMemo(
    () => ({
      startStreaming,
      stopStreaming,
      registerStreamCallbacks,
      setStreamedText,
    }),
    [startStreaming, stopStreaming, registerStreamCallbacks, setStreamedText]
  );

  return (
    <EventStreamingStateContext.Provider value={stateValue}>
      <EventStreamingControlsContext.Provider value={controlsValue}>
        {children}
      </EventStreamingControlsContext.Provider>
    </EventStreamingStateContext.Provider>
  );
};

export const EventStreamingWrapper = EventStreamingProvider;
